import { supabase } from '../lib/supabase';

const liveNotificationService = {
  // Insert a notification for a user
  async createNotification({ userId, notificationType, title, message, auctionId, metadata = {} }) {
    try {
      const { data, error } = await supabase?.from('live_notifications')?.insert([{
          user_id: userId,
          notification_type: notificationType,
          title,
          message,
          auction_id: auctionId || null,
          metadata
        }]);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating live notification:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get unread notifications for a user
  async getNotifications(userId, limit = 30) {
    try {
      const { data, error } = await supabase?.from('live_notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { data: [], error: error?.message };
    }
  },

  // Mark a single notification as read
  async markAsRead(notificationId) {
    try {
      const { error } = await supabase?.from('live_notifications')?.update({ is_read: true })?.eq('id', notificationId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // Mark all notifications as read for a user
  async markAllAsRead(userId) {
    try {
      const { error } = await supabase?.from('live_notifications')?.update({ is_read: true })?.eq('user_id', userId)?.eq('is_read', false);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // Poll for new notifications every 3 seconds.
  // Returns a handle object with a stop() method — replaces the old Realtime channel.
  subscribeToNotifications(userId, onNewNotification) {
    if (!userId) return null;

    let latestTimestamp = new Date()?.toISOString();
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await supabase
          ?.from('live_notifications')
          ?.select('*')
          ?.eq('user_id', userId)
          ?.gt('created_at', latestTimestamp)
          ?.order('created_at', { ascending: true });

        if (data && data?.length > 0) {
          latestTimestamp = data?.[data?.length - 1]?.created_at;
          data?.forEach(notification => {
            if (onNewNotification) onNewNotification(notification);
          });
        }
      } catch (err) {
        // Silently ignore poll errors to avoid console noise
      }
    };

    const intervalId = setInterval(poll, 3000);

    return {
      stop() {
        stopped = true;
        clearInterval(intervalId);
      }
    };
  },

  // Stop polling
  unsubscribe(handle) {
    if (handle && typeof handle?.stop === 'function') {
      handle?.stop();
    }
  }
};

export default liveNotificationService;
